// src/services/CartManager.js - Local-First Cart Manager
import logger from '../utils/logger';
import discountService from './discountService';

/**
 * CartManager - Manages cart operations using localStorage as source of truth
 *
 * Core Philosophy:
 * - localStorage is the ONLY source of truth during user session
 * - All operations are instant (no network calls for cart ops)
 * - Discounts are calculated immediately via API when items change
 * - Background sync handles server synchronization
 * - Never block user operations for network issues
 */

class CartManager {
  constructor() {
    // Storage keys
    this.CART_KEY = 'cart_data';
    this.QUEUE_KEY = 'cart_sync_queue';
    this.CONFIG_KEY = 'cart_config';

    // Event system for component re-renders
    this.listeners = new Set();

    // Configuration
    this.config = {
      version: 1,
      lastSyncTime: null,
      userId: null,
      sessionId: null
    };

    // Guard against HMR reloads - only initialize once
    this.initialized = false;

    // Debounce timer for discount calculation
    this._discountCalculationTimer = null;

    logger.log('[CartManager] Initialized local-first cart system');
  }

  /**
   * Schedule discount calculation with debounce
   * Server-only calculation - no local calc to avoid flicker issues
   */
  _scheduleDiscountCalculation() {
    if (this._discountTimer) {
      clearTimeout(this._discountTimer);
    }
    // Server calculation only (no local calc - it caused flicker issues)
    this._discountTimer = setTimeout(() => {
      this.calculateDiscounts();
    }, 300);
  }

  // Initialize cleanup (call this manually instead of in constructor)
  init() {
    if (this.initialized) {
      logger.log('[CartManager] Already initialized, skipping');
      return;
    }

    this.initialized = true;

    // Clean up any existing duplicates on initialization
    setTimeout(() => {
      this.cleanupDuplicates();
      this._monitorMemoryUsage();
    }, 100);

    logger.log('[CartManager] Initialization complete');
  }

  // === Event System for React Re-renders ===
  
  /**
   * Subscribe to cart changes
   * @param {Function} callback - Function to call when cart changes
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Unsubscribe from cart changes
   * @param {Function} callback - Function to remove
   */
  unsubscribe(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of cart changes
   * @private
   */
  _notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[CartManager] Error in listener callback:', error);
      }
    });
  }

  // === Core Cart Operations ===

  /**
   * Get current cart from localStorage
   * @returns {Object} Cart object with items, totals, etc.
   */
  getCart() {
    try {
      const cartData = localStorage.getItem(this.CART_KEY);
      if (!cartData) {
        return this._getEmptyCart();
      }

      const cart = JSON.parse(cartData);
      
      // Ensure cart has required structure
      if (!cart.items || !Array.isArray(cart.items)) {
        logger.warn('[CartManager] Invalid cart structure, resetting');
        return this._getEmptyCart();
      }

      // Debug logging
      logger.log(`[CartManager] Loading cart with ${cart.items.length} items from localStorage`);
      
      return {
        ...cart,
        total_items_count: cart.items.length,
        updated_at_iso: cart.updated_at_iso || new Date().toISOString()
      };
    } catch (error) {
      console.error('[CartManager] Error reading cart from localStorage:', error);
      return this._getEmptyCart();
    }
  }

  /**
   * Add item to cart
   * @param {Object} product - Product object
   * @param {Object} unit - Unit object  
   * @param {number} quantity - Quantity to add
   * @param {number} price - Price per unit (optional)
   * @returns {Object} Updated cart item
   */
  addItem(product, unit, quantity = 1, price = null) {
    try {
      const cart = this.getCart();
      const productId = product.id;
      const unitId = unit.id;
      
      // Find existing item
      const existingItemIndex = cart.items.findIndex(
        item => item.product?.id === productId && item.unit?.id === unitId
      );

      const priceToUse = price || parseFloat(product.price || 0);
      
      if (existingItemIndex >= 0) {
        // Update existing item - preserve discount data
        const existingItem = cart.items[existingItemIndex];
        const newQuantity = parseFloat(existingItem.quantity || 0) + parseFloat(quantity);

        // Use discounted price if available, otherwise use base price
        const effectiveUnitPrice = existingItem.discounted_price
          ? parseFloat(existingItem.discounted_price)
          : priceToUse;

        cart.items[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          line_total: newQuantity * effectiveUnitPrice,
          // Update line_total_before_discount if item has discount
          line_total_before_discount: existingItem.discount_percent > 0
            ? newQuantity * priceToUse
            : existingItem.line_total_before_discount
        };
      } else {
        // Add new item with temporary pending ID (for UI only, not synced)
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        const newItem = {
          id: tempId,
          server_id: null, // Will be set when server responds
          is_pending: true, // Indicates optimistic update
          product: {
            id: productId,
            name: product.name,
            price: product.price,
            images: product.images || [],
            category: product.category,
            min_quantity: product.min_quantity,
            step_quantity: product.step_quantity
          },
          unit: {
            id: unitId,
            symbol: unit.symbol,
            conversion_factor: unit.conversion_factor || 1
          },
          quantity: parseFloat(quantity),
          price_at_add: priceToUse,
          line_total: parseFloat(quantity) * priceToUse,
          added_at: new Date().toISOString()
        };
        
        cart.items.push(newItem);
      }

      // Update cart metadata
      this._updateCartMetadata(cart);
      
      // Save to localStorage
      this._saveCart(cart);
      
      // Queue for sync
      this._queueOperation({
        type: 'add',
        product_id: productId,
        unit_id: unitId,
        quantity: parseFloat(quantity),
        price: priceToUse,
        timestamp: Date.now()
      });

      // Trigger sync service to sync changes
      this._triggerSync();

      // Calculate discounts first (synchronous local calc), then notify UI
      this._scheduleDiscountCalculation();
      this._notifyListeners();

      logger.log(`[CartManager] Added ${quantity} x ${product.name} (${unit.symbol})`);

      return cart.items[existingItemIndex >= 0 ? existingItemIndex : cart.items.length - 1];
    } catch (error) {
      console.error('[CartManager] Error adding item:', error);
      throw error;
    }
  }

  /**
   * Update item quantity
   * @param {string} itemId - Local item ID
   * @param {number} newQuantity - New quantity
   * @returns {Object} Updated cart item or null if removed
   */
  updateItem(itemId, newQuantity) {
    try {
      const cart = this.getCart();
      const itemIndex = cart.items.findIndex(item => item.id === itemId);

      if (itemIndex === -1) {
        logger.warn(`[CartManager] Item ${itemId} not found for update`);
        return null;
      }

      const item = cart.items[itemIndex];
      const quantity = parseFloat(newQuantity);

      if (isNaN(quantity) || quantity < 0) {
        return this.removeItem(itemId);
      }

      // Update item - recalculate line_total using discounted price if available
      const effectiveUnitPrice = item.discounted_price
        ? parseFloat(item.discounted_price)
        : parseFloat(item.price_at_add || 0);
      cart.items[itemIndex] = {
        ...item,
        quantity: quantity,
        line_total: quantity * effectiveUnitPrice,
        // Also update line_total_before_discount for proper savings display
        line_total_before_discount: quantity * parseFloat(item.price_at_add || 0)
      };

      // Update cart metadata
      this._updateCartMetadata(cart);
      
      // Save to localStorage
      this._saveCart(cart);
      
      // Queue for sync. A pending item has no server row id yet, but its
      // quantity change must still reach the server — as an absolute 'set'
      // by product/unit. Silently skipping here was how "added 6 trays"
      // became 1 on the server.
      if (item.server_id) {
        this._queueOperation({
          type: 'update',
          item_id: item.server_id,
          quantity: quantity,
          timestamp: Date.now()
        });

        // Trigger sync service to sync changes
        this._triggerSync();
      } else if (item.product?.id && item.unit?.id) {
        this._queueOperation({
          type: 'set',
          product_id: item.product.id,
          unit_id: item.unit.id,
          quantity: quantity,
          price: parseFloat(item.price_at_add || 0),
          timestamp: Date.now(),
          replace_pending: true
        });

        this._triggerSync();
      } else {
        logger.warn(`[CartManager] Item ${itemId} has no server_id and no product/unit ids - cannot sync update`);
      }

      // Calculate discounts first (synchronous local calc), then notify UI
      this._scheduleDiscountCalculation();
      this._notifyListeners();

      logger.log(`[CartManager] Updated ${item.product?.name} quantity to ${quantity}`);

      return cart.items[itemIndex];
    } catch (error) {
      console.error('[CartManager] Error updating item:', error);
      throw error;
    }
  }

  /**
   * Set item quantity directly (not add)
   * This is for when user enters a specific quantity to SET, not ADD
   * @param {Object} product - Product object
   * @param {Object} unit - Unit object
   * @param {number} quantity - Quantity to set
   * @param {number} price - Price per unit (optional)
   * @returns {Object|null} Updated cart item or null if removed
   */
  setItemQuantity(product, unit, quantity, price = null) {
    try {
      const cart = this.getCart();
      const productId = product.id;
      const unitId = unit.id;
      const parsedQuantity = parseFloat(quantity);

      // Find existing item
      const existingItemIndex = cart.items.findIndex(
        item => item.product?.id === productId && item.unit?.id === unitId
      );

      const priceToUse = price || parseFloat(product.price || 0);

      // If quantity is 0 or negative, remove the item
      if (parsedQuantity <= 0) {
        if (existingItemIndex >= 0) {
          return this.removeItem(cart.items[existingItemIndex].id);
        }
        return null;
      }

      if (existingItemIndex >= 0) {
        // Update existing item - SET quantity (not add), preserve discount data
        const existingItem = cart.items[existingItemIndex];

        // Use discounted price if available, otherwise use base price
        const effectiveUnitPrice = existingItem.discounted_price
          ? parseFloat(existingItem.discounted_price)
          : priceToUse;

        cart.items[existingItemIndex] = {
          ...existingItem,
          quantity: parsedQuantity,
          line_total: parsedQuantity * effectiveUnitPrice,
          // Update line_total_before_discount if item has discount
          line_total_before_discount: existingItem.discount_percent > 0
            ? parsedQuantity * priceToUse
            : existingItem.line_total_before_discount
        };

        // Update cart metadata
        this._updateCartMetadata(cart);

        // Save to localStorage
        this._saveCart(cart);

        // Queue for sync
        if (existingItem.server_id) {
          // Item exists on server - use update operation
          this._queueOperation({
            type: 'update',
            item_id: existingItem.server_id,
            quantity: parsedQuantity,
            timestamp: Date.now()
          });
        } else {
          // No server_id yet — send the absolute quantity as a 'set' op.
          // A replacement 'add' would double-count if the original add was
          // already in flight (server adds increment); 'set' is idempotent.
          this._queueOperation({
            type: 'set',
            product_id: productId,
            unit_id: unitId,
            quantity: parsedQuantity,
            price: priceToUse,
            timestamp: Date.now(),
            replace_pending: true
          });
        }

        // Trigger sync service
        this._triggerSync();

        // Calculate discounts first (synchronous), then notify UI
        this._scheduleDiscountCalculation();
        this._notifyListeners();

        logger.log(`[CartManager] Set ${product.name} (${unit.symbol}) quantity to ${parsedQuantity}`);

        return cart.items[existingItemIndex];
      } else {
        // Add new item with the specified quantity
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        const newItem = {
          id: tempId,
          server_id: null,
          is_pending: true,
          product: {
            id: productId,
            name: product.name,
            price: product.price,
            images: product.images || [],
            category: product.category,
            min_quantity: product.min_quantity,
            step_quantity: product.step_quantity
          },
          unit: {
            id: unitId,
            symbol: unit.symbol,
            conversion_factor: unit.conversion_factor || 1
          },
          quantity: parsedQuantity,
          price_at_add: priceToUse,
          line_total: parsedQuantity * priceToUse,
          added_at: new Date().toISOString()
        };

        cart.items.push(newItem);

        // Update cart metadata
        this._updateCartMetadata(cart);

        // Save to localStorage
        this._saveCart(cart);

        // Queue for sync — 'set' carries the absolute intent and is
        // idempotent, so re-entering a quantity can never double-count.
        this._queueOperation({
          type: 'set',
          product_id: productId,
          unit_id: unitId,
          quantity: parsedQuantity,
          price: priceToUse,
          timestamp: Date.now(),
          replace_pending: true
        });

        // Trigger sync service
        this._triggerSync();

        // Notify listeners
        this._notifyListeners();

        // Immediately calculate discounts for responsive feedback
        this._scheduleDiscountCalculation();

        logger.log(`[CartManager] Added ${product.name} (${unit.symbol}) with quantity ${parsedQuantity}`);

        return newItem;
      }
    } catch (error) {
      console.error('[CartManager] Error setting item quantity:', error);
      throw error;
    }
  }

  /**
   * Remove item from cart
   * @param {string} itemId - Local item ID
   * @returns {boolean} Success status
   */
  removeItem(itemId) {
    try {
      const cart = this.getCart();
      const itemIndex = cart.items.findIndex(item => item.id === itemId);

      if (itemIndex === -1) {
        logger.warn(`[CartManager] Item ${itemId} not found for removal`);
        return false;
      }

      const item = cart.items[itemIndex];

      // Remove item
      cart.items.splice(itemIndex, 1);

      // Update cart metadata
      this._updateCartMetadata(cart);

      // Save to localStorage
      this._saveCart(cart);

      // Queue for sync only if item has server ID
      if (item.server_id) {
        this._queueOperation({
          type: 'remove',
          item_id: item.server_id,
          timestamp: Date.now()
        });

        // Trigger sync service to sync changes
        this._triggerSync();
      } else if (!item.is_pending) {
        logger.warn(`[CartManager] Item ${itemId} has no server_id and is not pending - cannot sync removal`);
      }

      // Recalculate discounts - PRODUCT_COMBO conditions may no longer be met
      this._scheduleDiscountCalculation();

      // Notify listeners
      this._notifyListeners();

      logger.log(`[CartManager] Removed ${item.product?.name} from cart`);

      return true;
    } catch (error) {
      console.error('[CartManager] Error removing item:', error);
      throw error;
    }
  }

  /**
   * Clear entire cart
   * @returns {boolean} Success status
   */
  clearCart() {
    try {
      // Fresh cart with a NEW uuid. _getEmptyCart() already generates a new id;
      // we used to `delete emptyCart.id` here to avoid re-deduping against the
      // just-completed order, but that left the next cart with no id at all —
      // so the next checkout's cart_uuid was empty and backend dedup was
      // bypassed entirely. The new id from _newCartId() is guaranteed distinct
      // from the previous one, so it solves both problems.
      const emptyCart = this._getEmptyCart();

      // Save empty cart
      this._saveCart(emptyCart);

      // Clear sync queue
      this._clearSyncQueue();

      // Queue clear operation for sync
      this._queueOperation({
        type: 'clear',
        timestamp: Date.now()
      });

      // Trigger sync service to sync changes
      this._triggerSync();

      // Notify listeners
      this._notifyListeners();

      logger.log('[CartManager] Cart cleared - items removed:', emptyCart.items.length);

      return true;
    } catch (error) {
      console.error('[CartManager] Error clearing cart:', error);
      throw error;
    }
  }

  // === Sync Queue Management ===

  /**
   * Queue operation for background sync
   * @param {Object} operation - Operation to queue
   * @private
   */
  _queueOperation(operation) {
    try {
      let queue = this._getSyncQueue();

      // If replace_pending is true, remove any queued add/set operation for
      // the same product/unit — the new absolute op supersedes them.
      if (operation.replace_pending && (operation.type === 'add' || operation.type === 'set')) {
        queue = queue.filter(op => {
          if ((op.type === 'add' || op.type === 'set') &&
              op.product_id === operation.product_id &&
              op.unit_id === operation.unit_id) {
            logger.log(`[CartManager] Replacing pending ${op.type} operation for product ${operation.product_id}`);
            return false;
          }
          return true;
        });
      }

      // Remove the replace_pending flag before storing
      const { replace_pending, ...opToStore } = operation;

      queue.push({
        ...opToStore,
        id: `op_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        queued_at: Date.now()
      });

      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      logger.error('[CartManager] Error queuing operation:', error);
    }
  }

  /**
   * Get sync queue
   * @returns {Array} Queue of operations
   */
  getSyncQueue() {
    return this._getSyncQueue();
  }

  /**
   * Get sync queue from localStorage
   * @returns {Array} Queue of operations
   * @private
   */
  _getSyncQueue() {
    try {
      const queueData = localStorage.getItem(this.QUEUE_KEY);
      const queue = queueData ? JSON.parse(queueData) : [];

      if (queue.length > 0) {
        logger.log(`[CartManager] Sync queue has ${queue.length} operations:`, queue.map(op => op.type));
      }

      return queue;
    } catch (error) {
      logger.error('[CartManager] Error reading sync queue:', error);
      return [];
    }
  }

  /**
   * Clear sync queue
   * @private
   */
  _clearSyncQueue() {
    try {
      localStorage.removeItem(this.QUEUE_KEY);
    } catch (error) {
      logger.error('[CartManager] Error clearing sync queue:', error);
    }
  }

  /**
   * Remove processed operations from queue
   * @param {Array} operationIds - IDs of operations to remove
   */
  removeFromSyncQueue(operationIds) {
    try {
      const queue = this._getSyncQueue();
      const filteredQueue = queue.filter(op => !operationIds.includes(op.id));
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(filteredQueue));
    } catch (error) {
      logger.error('[CartManager] Error removing from sync queue:', error);
    }
  }

  // === Server Sync Integration ===

  /**
   * Handle server response after adding an item
   * Maps temporary item to server-assigned ID
   * NOTE: Preserves locally calculated discount data - only updates server ID
   * @param {string} tempId - Temporary local ID
   * @param {Object} serverItem - Item data from server with real ID
   */
  handleServerAddResponse(tempId, serverItem) {
    try {
      const cart = this.getCart();
      const itemIndex = cart.items.findIndex(item => item.id === tempId);

      if (itemIndex === -1) {
        logger.warn(`[CartManager] Temp item ${tempId} not found for server mapping`);
        return;
      }

      const existingItem = cart.items[itemIndex];

      // Update item with server ID only - PRESERVE local discount data
      cart.items[itemIndex] = {
        ...existingItem,
        id: `server_${serverItem.id}`, // Use prefixed server ID for UI
        server_id: serverItem.id, // Store raw server ID for API calls
        is_pending: false,
        // Only update price if not already set
        price_at_add: existingItem.price_at_add || serverItem.price_at_add,
        // PRESERVE existing discount data - don't overwrite with server data
        // Discounts are calculated locally via calculateDiscounts()
      };

      this._saveCart(cart);
      this._notifyListeners();

      logger.log(`[CartManager] Mapped temp item ${tempId} to server ID ${serverItem.id} (preserved discount data)`);
    } catch (error) {
      logger.error('[CartManager] Error handling server add response:', error);
    }
  }

  /**
   * Handle failed server operation by marking item as failed
   * @param {string} tempId - Temporary local ID
   * @param {string} error - Error message
   */
  handleServerAddFailure(tempId, error) {
    try {
      const cart = this.getCart();
      const itemIndex = cart.items.findIndex(item => item.id === tempId);

      if (itemIndex === -1) {
        logger.warn(`[CartManager] Temp item ${tempId} not found for failure handling`);
        return;
      }

      // Mark item as failed
      cart.items[itemIndex] = {
        ...cart.items[itemIndex],
        is_pending: false,
        is_failed: true,
        error_message: error
      };

      this._saveCart(cart);
      this._notifyListeners();

      logger.error(`[CartManager] Marked item ${tempId} as failed: ${error}`);
    } catch (error) {
      logger.error('[CartManager] Error handling server add failure:', error);
    }
  }

  /**
   * Stable per-browser device id for cart audit attribution. Generated once,
   * survives logout cleanup deliberately — it identifies the device, not the
   * session.
   * @returns {string|null}
   */
  getDeviceId() {
    try {
      let deviceId = localStorage.getItem('device_id');
      if (!deviceId) {
        const uuid = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        deviceId = `dev-${uuid}`;
        localStorage.setItem('device_id', deviceId);
      }
      return deviceId;
    } catch (error) {
      logger.error('[CartManager] Error getting device id:', error);
      return null;
    }
  }

  /**
   * Adopt the server cart row id as the local cart id (checkout dedup key).
   * The server cart is one object all of a user's devices sync into, so a
   * shared key lets the backend's unique constraint reject a duplicate
   * checkout from a second device and return the existing order instead.
   * @param {number|string} serverCartId - Server cart row id
   */
  adoptServerCartId(serverCartId) {
    try {
      if (!serverCartId) return;
      const sharedId = `server-cart-${serverCartId}`;
      const cart = this.getCart();
      if (cart.id === sharedId) return;
      cart.id = sharedId;
      this._saveCart(cart);
      this._notifyListeners();
      logger.log(`[CartManager] Adopted shared checkout id ${sharedId}`);
    } catch (error) {
      logger.error('[CartManager] Error adopting server cart id:', error);
    }
  }

  /**
   * Replace local cart with server cart data
   * Converts server IDs to the expected format
   * @param {Object} serverCart - Cart data from server
   */
  replaceWithServerCart(serverCart) {
    try {
      // Convert server cart items to our format
      const convertedItems = serverCart.items.map(serverItem => {
        const hasDiscount = serverItem.discounted_price && parseFloat(serverItem.discount_percent) > 0;
        const priceAtAdd = parseFloat(serverItem.price_at_add);
        const effectivePrice = hasDiscount ? parseFloat(serverItem.discounted_price) : priceAtAdd;

        // Ensure unit has symbol property (handle cases where server might return {id, name} only).
        // The conversion factor is PER LINE (ProductExtraUnit), served on the
        // item — the global unit has none. Falling back to 1 here was how
        // adopted pcs items got per-kg prices rendered as per-pcs.
        const itemConversionFactor =
          parseFloat(serverItem.conversion_factor) ||
          parseFloat(serverItem.unit?.conversion_factor) || 1;
        const normalizedUnit = serverItem.unit ? {
          id: serverItem.unit.id,
          symbol: serverItem.unit.symbol || serverItem.unit.name || 'pcs',
          conversion_factor: itemConversionFactor
        } : { id: null, symbol: 'pcs', conversion_factor: itemConversionFactor };

        return {
          id: `server_${serverItem.id}`, // Prefixed for UI consistency
          server_id: serverItem.id, // Raw server ID for API calls
          is_pending: false,
          is_failed: false,
          product: serverItem.product,
          unit: normalizedUnit,
          quantity: parseFloat(serverItem.quantity),
          price_at_add: priceAtAdd,
          line_total: parseFloat(serverItem.line_total || serverItem.quantity * effectivePrice),
          added_at: serverItem.added_at || new Date().toISOString(),
          // Discount fields from server
          discount_applied: serverItem.discount_applied || null,
          discount_percent: serverItem.discount_percent || 0,
          discounted_price: serverItem.discounted_price || null,
          discount_reason: serverItem.discount_reason || '',
          discount_name: serverItem.discount_name || '',
          line_total_before_discount: serverItem.line_total_before_discount || null,
          line_discount_amount: serverItem.line_discount_amount || null
        };
      });

      // Adopt the server cart row id as the checkout dedup key: it is shared
      // by every device syncing into the same active cart, so the backend's
      // unique constraint on Order.cart_uuid catches cross-device duplicate
      // orders. Stable across re-syncs of the same active cart; rotates
      // naturally when a new active cart is created after an order.
      const cart = {
        id: serverCart.id ? `server-cart-${serverCart.id}` : (this.getCart().id || this._newCartId()),
        items: convertedItems,
        total_price: parseFloat(serverCart.total_price || 0),
        total_items_count: convertedItems.length,
        updated_at_iso: serverCart.updated_at || new Date().toISOString()
      };

      this._saveCart(cart);
      this._notifyListeners();

      logger.log(`[CartManager] Replaced local cart with server cart (${convertedItems.length} items)`);
    } catch (error) {
      logger.error('[CartManager] Error replacing with server cart:', error);
    }
  }

  /**
   * Clean up duplicate items (same product, different units)
   * Keeps the item with the highest quantity
   */
  cleanupDuplicates() {
    try {
      const cart = this.getCart();
      const productMap = new Map();
      
      // Group items by product ID
      cart.items.forEach(item => {
        const productId = item.product?.id;
        if (!productId) return;
        
        if (!productMap.has(productId)) {
          productMap.set(productId, []);
        }
        productMap.get(productId).push(item);
      });
      
      // Check for duplicates
      let hasDuplicates = false;
      const cleanedItems = [];
      
      productMap.forEach((items, productId) => {
        if (items.length > 1) {
          hasDuplicates = true;
          logger.warn(`[CartManager] Found ${items.length} duplicate items for product ${items[0].product?.name}`);

          // Keep the item with highest quantity
          const itemToKeep = items.reduce((prev, current) => {
            const prevQty = parseFloat(prev.quantity || 0);
            const currQty = parseFloat(current.quantity || 0);
            return currQty > prevQty ? current : prev;
          });

          logger.log(`[CartManager] Keeping item with ${itemToKeep.quantity} ${itemToKeep.unit?.symbol}`);
          cleanedItems.push(itemToKeep);
        } else {
          cleanedItems.push(items[0]);
        }
      });

      if (hasDuplicates) {
        // Update cart with cleaned items
        cart.items = cleanedItems;
        this._updateCartMetadata(cart);
        this._saveCart(cart);
        this._notifyListeners();

        logger.log('[CartManager] Cleaned up duplicate items');
      }

      return hasDuplicates;
    } catch (error) {
      logger.error('[CartManager] Error cleaning up duplicates:', error);
      return false;
    }
  }


  /**
   * Update last sync time
   * @param {number} timestamp - Sync timestamp
   */
  updateLastSyncTime(timestamp = Date.now()) {
    this._updateConfig({ lastSyncTime: timestamp });
  }

  // === Sync Integration ===

  /**
   * Trigger sync service to sync cart changes
   * @private
   */
  _triggerSync() {
    try {
      // Import dynamically to avoid circular dependencies
      import('./SyncService.js').then(({ default: syncService }) => {
        syncService.triggerSync();
      });
    } catch (error) {
      logger.error('[CartManager] Error triggering sync:', error);
    }
  }

  // === Discount Calculation ===

  /**
   * Set the current customer ID for discount calculations
   * @param {number|null} customerId - Customer ID or null
   */
  setCustomerId(customerId) {
    this._customerId = customerId;
    logger.log('[CartManager] Customer ID set to:', customerId);
  }

  /**
   * Get the current customer ID
   * @returns {number|null} Customer ID or null
   */
  getCustomerId() {
    return this._customerId || null;
  }

  /**
   * Calculate discounts for all cart items immediately via API
   * Updates cart items with discount data and notifies listeners
   * @param {number|null} customerId - Optional customer ID override
   */
  async calculateDiscounts(customerId = null) {
    try {
      const cartBeforeApi = this.getCart();
      if (!cartBeforeApi.items || cartBeforeApi.items.length === 0) {
        logger.log('[CartManager] No items to calculate discounts for');
        return;
      }

      // Prepare items for bulk calculation
      // Send BOTH quantities:
      // - ordered_quantity: for MIN_QUANTITY rules (e.g., "minimum 3 trays")
      // - base_quantity: for price tiers and FIXED_PRICE (defined per kg)
      const items = cartBeforeApi.items.map(item => {
        const orderedQuantity = parseFloat(item.quantity || 0);
        const conversionFactor = parseFloat(item.unit?.conversion_factor || 1);
        const baseQuantity = orderedQuantity * conversionFactor;
        return {
          product_id: item.product?.id,
          quantity: baseQuantity,  // Base quantity (e.g., kg) for price tiers
          ordered_quantity: orderedQuantity,  // Ordered quantity (e.g., trays) for MIN_QUANTITY rules
          unit_price: parseFloat(item.product?.price || 0)  // Base product price per base unit
        };
      });

      // Use provided customerId, or stored customerId, or null
      const effectiveCustomerId = customerId || this._customerId || null;
      logger.log('[CartManager] Calculating discounts for', items.length, 'items, customer:', effectiveCustomerId);

      // Call bulk discount calculation API with customer ID
      const result = await discountService.bulkCalculateDiscounts(items, effectiveCustomerId);

      if (result && result.items) {
        // IMPORTANT: Re-fetch cart AFTER async API call to avoid stale closure issue
        // The cart may have been modified while waiting for the API response
        const cart = this.getCart();

        // Update each cart item with discount data
        let updated = false;
        cart.items.forEach((cartItem, index) => {
          const discountData = result.items.find(
            d => d.product_id === cartItem.product?.id
          );

          if (discountData) {
            // Extract discount name and ID from applied_discounts array
            const discountName = discountData.applied_discounts && discountData.applied_discounts.length > 0
              ? discountData.applied_discounts[0].discount_name
              : '';
            const discountId = discountData.applied_discounts && discountData.applied_discounts.length > 0
              ? discountData.applied_discounts[0].discount_id
              : null;

            // The API returns prices per BASE unit (e.g., per kg)
            // We need to convert to the item's unit using conversion_factor (e.g., 0.75 for 0.75kg tray)
            const conversionFactor = parseFloat(cartItem.unit?.conversion_factor || 1);
            const discountedUnitPrice = discountData.final_price ? discountData.final_price * conversionFactor : null;
            // Store the base discounted price (per kg) for FIXED_PRICE discounts
            const baseDiscountedPrice = discountData.final_price || null;

            // Calculate line totals using CURRENT quantity (from re-fetched cart), not stale quantity
            const quantity = parseFloat(cartItem.quantity || 0);
            const lineDiscounted = discountedUnitPrice ? quantity * discountedUnitPrice : null;
            const lineOriginal = parseFloat(cartItem.price_at_add || 0) * quantity;

            cart.items[index] = {
              ...cartItem,
              discount_percent: discountData.discount_percent || 0,
              discounted_price: discountedUnitPrice,  // Converted to item's unit (e.g., per tray)
              base_discounted_price: baseDiscountedPrice,  // Discounted price per base unit (e.g., per kg)
              discount_applied: discountId,  // Store the discount ID
              discount_name: discountName,
              discount_reason: discountName,
              line_total_before_discount: lineOriginal,
              line_discount_amount: discountData.has_discount && lineDiscounted ? lineOriginal - lineDiscounted : 0,
              // Update line_total to use discounted price if available
              line_total: discountData.has_discount && lineDiscounted
                ? lineDiscounted
                : lineOriginal
            };
            updated = true;

            logger.log(`[CartManager] Discount applied to ${cartItem.product?.name}: ${discountData.discount_percent}% off, name: ${discountName}, discounted unit price: ${discountedUnitPrice}, base discounted price: ${baseDiscountedPrice}`);
          }
        });

        if (updated) {
          this._updateCartMetadata(cart);
          this._saveCart(cart);
          this._notifyListeners();
          logger.log('[CartManager] Updated cart with discount data');
        }
      }
    } catch (error) {
      logger.error('[CartManager] Error calculating discounts:', error);
      // Don't throw - discount calculation failure shouldn't break cart
    }
  }

  /**
   * Recalculate base prices for all cart items based on customer's price type
   * Called when customer changes to update prices before discount calculation
   * @param {number|null} customerId - Customer ID or null
   */
  async recalculatePricesForCustomer(customerId = null) {
    try {
      const cart = this.getCart();
      if (!cart.items || cart.items.length === 0) {
        logger.log('[CartManager] No items to recalculate prices for');
        return;
      }

      // Get product IDs from cart
      const productIds = cart.items.map(item => item.product?.id).filter(id => id != null);

      if (productIds.length === 0) {
        logger.log('[CartManager] No valid product IDs in cart');
        return;
      }

      logger.log('[CartManager] Recalculating prices for customer:', customerId, 'products:', productIds);

      // Fetch customer-specific prices from backend
      const { default: apiClient } = await import('./api');
      const response = await apiClient.post('/orders/get_customer_prices/', {
        product_ids: productIds,
        customer_id: customerId || null
      });

      const customerPrices = response.data;
      logger.log('[CartManager] Customer prices received:', customerPrices);

      // Update each cart item with new price
      let updated = false;
      cart.items.forEach((item, index) => {
        const priceInfo = customerPrices[item.product?.id?.toString()];
        if (priceInfo) {
          const newBasePrice = parseFloat(priceInfo.price);
          const conversionFactor = parseFloat(item.unit?.conversion_factor || 1);
          const newUnitPrice = newBasePrice * conversionFactor;
          const quantity = parseFloat(item.quantity || 0);

          // Update the item with new price
          cart.items[index] = {
            ...item,
            product: {
              ...item.product,
              price: newBasePrice  // Update stored product price
            },
            price_at_add: newUnitPrice,  // Update price per unit
            line_total: quantity * newUnitPrice,
            line_total_before_discount: quantity * newUnitPrice,
            // Clear discount data - will be recalculated
            discount_percent: 0,
            discounted_price: null,
            base_discounted_price: null,
            discount_applied: null,
            discount_name: '',
            discount_reason: '',
            line_discount_amount: 0
          };
          updated = true;

          logger.log(`[CartManager] Updated price for ${item.product?.name}: £${newUnitPrice.toFixed(2)} (base: £${newBasePrice.toFixed(2)})`);
        }
      });

      if (updated) {
        this._updateCartMetadata(cart);
        this._saveCart(cart);
        this._notifyListeners();
        logger.log('[CartManager] Cart prices updated for customer');

        // Now calculate discounts on top of new prices
        await this.calculateDiscounts(customerId);
      }
    } catch (error) {
      logger.error('[CartManager] Error recalculating prices for customer:', error);
      // Don't throw - price recalculation failure shouldn't break cart
    }
  }

  // === Helper Methods ===

  /**
   * Get empty cart structure
   * @returns {Object} Empty cart
   * @private
   */
  _getEmptyCart() {
    return {
      id: this._newCartId(),
      items: [],
      total_price: '0.00',
      total_items_count: 0,
      updated_at_iso: new Date().toISOString(),
      version: this.config.version
    };
  }

  _newCartId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `cart_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Update cart metadata (totals, timestamps)
   * @param {Object} cart - Cart to update
   * @private
   */
  _updateCartMetadata(cart) {
    // Calculate total price
    const totalPrice = cart.items.reduce((sum, item) => {
      return sum + parseFloat(item.line_total || 0);
    }, 0);

    cart.total_price = totalPrice.toFixed(2);
    cart.total_items_count = cart.items.length;
    cart.updated_at_iso = new Date().toISOString();
  }

  /**
   * Save cart to localStorage
   * @param {Object} cart - Cart to save
   * @private
   */
  _saveCart(cart) {
    try {
      logger.log('[CartManager] Saving cart with items:', cart.items.map(i => ({
        id: i.id,
        product: i.product?.name,
        quantity: i.quantity
      })));

      localStorage.setItem(this.CART_KEY, JSON.stringify(cart));
    } catch (error) {
      logger.error('[CartManager] Error saving cart to localStorage:', error);
      throw error;
    }
  }

  /**
   * Update configuration
   * @param {Object} updates - Config updates
   * @private
   */
  _updateConfig(updates) {
    this.config = { ...this.config, ...updates };
    try {
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config));
    } catch (error) {
      logger.error('[CartManager] Error saving config:', error);
    }
  }

  /**
   * Get configuration
   * @returns {Object} Current config
   */
  getConfig() {
    try {
      const configData = localStorage.getItem(this.CONFIG_KEY);
      if (configData) {
        this.config = { ...this.config, ...JSON.parse(configData) };
      }
    } catch (error) {
      logger.error('[CartManager] Error reading config:', error);
    }
    return this.config;
  }

  /**
   * Initialize for user session
   * @param {string} userId - User ID
   */
  initializeForUser(userId) {
    this._updateConfig({
      userId,
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
    });
    logger.log(`[CartManager] Initialized for user: ${userId}`);
  }

  /**
   * Clean up on logout
   */
  /**
   * Monitor cart memory usage and cleanup if needed
   * @private
   */
  _monitorMemoryUsage() {
    try {
      const cartData = localStorage.getItem(this.CART_KEY);
      const queueData = localStorage.getItem(this.QUEUE_KEY);
      
      if (cartData) {
        const cartSize = cartData.length;
        const cartSizeKB = Math.round(cartSize / 1024);

        if (cartSizeKB > 1024) { // If cart data > 1MB
          logger.warn(`[CartManager] Large cart data detected (${cartSizeKB}KB)`);

          // Try to optimize the cart
          this._optimizeCartData();
        }
      }

      if (queueData) {
        const queueSize = queueData.length;
        const queueSizeKB = Math.round(queueSize / 1024);

        if (queueSizeKB > 512) { // If queue > 512KB
          logger.warn(`[CartManager] Large sync queue detected (${queueSizeKB}KB), clearing old items`);
          this._optimizeSyncQueue();
        }
      }

    } catch (error) {
      logger.error('[CartManager] Error monitoring memory usage:', error);
    }
  }

  /**
   * Optimize cart data to reduce memory usage
   * @private
   */
  _optimizeCartData() {
    try {
      const cart = this.getCart();
      let optimized = false;
      
      // Remove items with zero quantity
      const beforeCount = cart.items.length;
      cart.items = cart.items.filter(item => {
        const qty = parseFloat(item.quantity || 0);
        return qty > 0;
      });
      
      if (cart.items.length < beforeCount) {
        optimized = true;
        logger.log(`[CartManager] Removed ${beforeCount - cart.items.length} zero-quantity items`);
      }

      // Cleanup old metadata
      if (cart.metadata && cart.metadata.length > 100) {
        cart.metadata = cart.metadata.slice(-50); // Keep only last 50 entries
        optimized = true;
        logger.log('[CartManager] Trimmed metadata history');
      }

      if (optimized) {
        this._updateCartMetadata(cart);
        this._saveCart(cart);
      }

    } catch (error) {
      logger.error('[CartManager] Error optimizing cart data:', error);
    }
  }

  /**
   * Optimize sync queue to prevent memory bloat
   * @private
   */
  _optimizeSyncQueue() {
    try {
      const queueData = localStorage.getItem(this.QUEUE_KEY);
      if (!queueData) return;
      
      const queue = JSON.parse(queueData);
      if (!Array.isArray(queue)) return;
      
      // Keep only recent operations (last 24 hours)
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      const filteredQueue = queue.filter(op => {
        return op.timestamp && op.timestamp > oneDayAgo;
      });
      
      // Also limit to maximum 100 operations
      const limitedQueue = filteredQueue.slice(-100);
      
      if (limitedQueue.length < queue.length) {
        localStorage.setItem(this.QUEUE_KEY, JSON.stringify(limitedQueue));
        logger.log(`[CartManager] Optimized sync queue: ${queue.length} -> ${limitedQueue.length} operations`);
      }

    } catch (error) {
      logger.error('[CartManager] Error optimizing sync queue:', error);
    }
  }

  /**
   * Get sync queue size for monitoring
   * @returns {number} Queue size
   */
  getSyncQueueSize() {
    try {
      const queueData = localStorage.getItem(this.QUEUE_KEY);
      if (!queueData) return 0;
      
      const queue = JSON.parse(queueData);
      return Array.isArray(queue) ? queue.length : 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Clear sync queue
   */
  clearSyncQueue() {
    try {
      localStorage.removeItem(this.QUEUE_KEY);
      logger.log('[CartManager] Cleared sync queue');
    } catch (error) {
      logger.error('[CartManager] Error clearing sync queue:', error);
    }
  }

  cleanup() {
    try {
      logger.log('[CartManager] Clearing all cart data on logout');

      localStorage.removeItem(this.CART_KEY);
      localStorage.removeItem(this.QUEUE_KEY);
      localStorage.removeItem(this.CONFIG_KEY);

      this._notifyListeners();

      logger.log('[CartManager] Cleaned up for logout - all cart keys removed');
    } catch (error) {
      logger.error('[CartManager] Error during cleanup:', error);
    }
  }
}

// Create singleton instance
const cartManager = new CartManager();

export default cartManager;